import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { CLINIC } from "../lib/clinic";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "doctors", label: "Doctors" },
  { id: "reviews", label: "Reviews" },
  { id: "gallery", label: "Gallery" },
  { id: "contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.06)] border-b border-[#009688]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <button
            data-testid="navbar-logo"
            onClick={() => go("home")}
            className="flex items-center gap-2.5 group"
          >
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#009688] to-[#00796b] grid place-items-center shadow-lg shadow-[#009688]/25 group-hover:scale-105 transition-transform">
              <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 3c-2.5 0-3.5 1-5 1-2 0-3.5 1.5-3.5 5 0 3 1 6 2 8s1.5 4 3 4 1.5-4 3.5-4 2 4 3.5 4 2-2 3-4 2-5 2-8c0-3.5-1.5-5-3.5-5-1.5 0-2.5-1-5-1z"
                  fill="white"
                />
              </svg>
            </div>
            <div className="flex flex-col leading-none text-left">
              <span className="font-[Outfit] text-[17px] font-bold tracking-tight text-[#1F2937]">
                Abhayam
              </span>
              <span className="text-[11px] text-[#009688] font-semibold tracking-wide">
                DENTAL CLINIC
              </span>
            </div>
          </button>

          {/* Desktop */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="navbar-links">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  active === l.id
                    ? "text-[#009688] bg-[#009688]/8"
                    : "text-[#1F2937]/70 hover:text-[#009688] hover:bg-[#009688]/5"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${CLINIC.phoneRaw}`}
              data-testid="navbar-call"
              className="flex items-center gap-2 text-sm font-semibold text-[#1F2937] hover:text-[#009688] transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={2.5} />
              {CLINIC.phone}
            </a>
            <button
              data-testid="navbar-book-btn"
              onClick={() => go("appointment")}
              className="px-5 py-2.5 rounded-full bg-[#009688] text-white text-sm font-semibold hover:bg-[#00796b] active:scale-95 transition-all shadow-lg shadow-[#009688]/25"
            >
              Book Now
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden w-11 h-11 rounded-full grid place-items-center bg-white shadow-md border border-[#009688]/10"
          >
            {open ? (
              <X className="w-5 h-5 text-[#1F2937]" />
            ) : (
              <Menu className="w-5 h-5 text-[#1F2937]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          open ? "max-h-[600px] border-t border-[#009688]/10" : "max-h-0"
        } bg-white/95 backdrop-blur-xl`}
      >
        <nav className="px-4 py-5 flex flex-col gap-1" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`mobile-nav-link-${l.id}`}
              className={`text-left px-4 py-3 rounded-xl text-[15px] font-medium ${
                active === l.id
                  ? "bg-[#009688]/10 text-[#009688]"
                  : "text-[#1F2937]"
              }`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("appointment")}
            data-testid="mobile-book-btn"
            className="mt-2 px-4 py-3 rounded-full bg-[#009688] text-white font-semibold"
          >
            Book Appointment
          </button>
          <a
            href={`tel:${CLINIC.phoneRaw}`}
            data-testid="mobile-call-btn"
            className="px-4 py-3 rounded-full border border-[#009688]/30 text-[#009688] font-semibold flex items-center justify-center gap-2"
          >
            <Phone className="w-4 h-4" /> {CLINIC.phone}
          </a>
        </nav>
      </div>
    </header>
  );
};
